import type { ContentEngine } from "./engine";
import type { ContentRegistry } from "./registry";
import { slugify } from "./parser";

/**
 * Game content a host provides to the sidebar tabs: the plugin's vault folders.
 * Tabs inject it under CONTENT_SOURCE_KEY and fall back to the bundled tables
 * and sentence code when it is absent (the web app) or lacks what they need.
 */
export interface ContentSource {
  readonly registry: ContentRegistry;
  readonly engine: ContentEngine;
  /** Setting used when nothing more specific is chosen. */
  defaultSetting(): string;
  /** Called after content changes (files edited, folders changed). Returns an unsubscribe. */
  onChange(fn: () => void): () => void;
}

export const CONTENT_SOURCE_KEY = "contentSource";

/** Settings the bundled sentence code knows. */
const BUNDLED_SETTINGS = new Set(["fantasy", "modern", "monster-hunter"]);

/**
 * Whether content for this setting should come from the source. False for a
 * built-in setting it has nothing for yet (modern, monster-hunter), which the
 * bundled code still covers — generating from base content alone would render
 * as `[missing: …]`.
 */
export function coversSetting(source: ContentSource, setting: string | undefined): boolean {
  if (!setting) return true;
  const slug = slugify(setting);
  return !BUNDLED_SETTINGS.has(slug) || source.registry.settings().includes(slug);
}

/** A resolution roll's effect text, or undefined when the source has no such type or row. */
export function resolutionEffectFrom(
  source: ContentSource,
  type: string,
  roll: number,
  dc: number,
  setting?: string,
): string | undefined {
  const r = source.engine.resolve(type, roll, { setting, params: { dc } });
  if (!r?.row) return undefined;
  return r.row.cells.effect ?? r.row.cells.result;
}

/** Distinct keywords from the source's `keyword` table, or undefined if it has none. */
export function keywordsFrom(source: ContentSource, count = 3): string[] | undefined {
  if (!source.registry.getTable("keyword")) return undefined;
  return source.engine
    .expand(`{${count} unique keyword}`)
    .text.split(", ")
    .filter(Boolean);
}

/** Resolution types in the source, spelled as `resolve:` and the tab use them ("combat strike"). */
export function resolutionTypesFrom(source: ContentSource): { id: string; label: string; description: string }[] {
  return source.registry.list("resolution").map((e) => {
    const firstLine = e.kind === "generator" ? "" : e.description.split("\n")[0] ?? "";
    // The starter set writes "**Combat: Attack** — Phase 3 …"; use the bold part as the label.
    const bold = firstLine.match(/^\*\*(.+?)\*\*\s*(?:—\s*)?(.*)$/);
    return {
      id: e.name.replace(/-/g, " "),
      label: bold ? bold[1] : e.label,
      description: bold ? bold[2] : firstLine,
    };
  });
}

export interface GeneratorOption {
  /** Param name the generator reads, e.g. "creature-type". */
  name: string;
  label: string;
  values: string[];
}

/** Tables with more distinct values than this make poor dropdowns and are not offered. */
const MAX_OPTION_VALUES = 40;

/**
 * The choices a generator offers: every table it reuses with `{=name}` or
 * `[name]`. Passing one of the values as a param fixes it instead of rolling —
 * the Creature generator's `{=creature-type}` becomes a "Creature Type" picker.
 */
export function generatorOptions(source: ContentSource, name: string, setting?: string): GeneratorOption[] {
  const generator = source.registry.getGenerator(name, setting);
  if (!generator) return [];
  const names: string[] = [];
  for (const [, inner] of generator.body.matchAll(/\{([^{}]+)\}/g)) {
    const reused = inner.trim().match(/^=\s*([^.|?\s]+)/);
    if (reused) names.push(slugify(reused[1]));
    for (const [, dep] of inner.matchAll(/\[([^\]]+)\]/g)) names.push(slugify(dep));
  }
  const options: GeneratorOption[] = [];
  for (const param of [...new Set(names)]) {
    const table = source.registry.getTable(param, setting);
    if (!table) continue;
    const values = [...new Set(table.rows.map((r) => r.cells.result).filter(Boolean))];
    if (values.length > MAX_OPTION_VALUES) continue;
    options.push({ name: param, label: table.label, values });
  }
  return options;
}
